import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { sessionClearedEvent, sessionExpiredEvent } from "./auth.ts";
import { appCallbackPathFromDeepLink, createNativeLoginChallenge } from "./native-auth-link.ts";
import { createNativeFetch } from "./native-session.ts";
import { initializeSecureStorage, nativeSession, readNativeLogin, storeNativeLogin, clearNativeLogin } from "./native-secure-storage.ts";
import { clearNativeDownloads } from "./native-files.ts";
export { downloadNativeFile, shareNativeFile, openNativeFile } from "./native-files.ts";

export const nativeBackEvent = "edubgt:native-back";
export const nativeResumeEvent = "edubgt:native-resume";
export const nativeErrorEvent = "edubgt:native-error";
let initialized = false;
let handlingLink = false;

export function isNativeApp() { return Capacitor.isNativePlatform(); }

function apiOrigin() {
  const url = new URL(import.meta.env?.VITE_API_URL || "https://edubgt.bgtschoolmediac070de.workers.dev");
  if (url.protocol !== "https:") throw new Error("Konfigurimi i aplikacionit nuk është i vlefshëm.");
  return url.origin;
}

function reportNativeError(message: string) {
  window.dispatchEvent(new CustomEvent(nativeErrorEvent, { detail: message }));
}

async function openAppUrl(url: string) {
  const path = appCallbackPathFromDeepLink(url);
  if (!path || handlingLink) return;
  const login = await readNativeLogin();
  if (!login) {
    reportNativeError("Kyçja ka skaduar. Provo përsëri.");
    return;
  }
  handlingLink = true;
  await Browser.close().catch(() => undefined);
  const callback = new URL(path, window.location.href);
  for (const [key, value] of new URLSearchParams(login.callbackQuery)) {
    if (key !== "code" && key !== "error") callback.searchParams.set(key, value);
  }
  window.location.replace(`${callback.pathname}${callback.search}`);
}

export async function initNativeApp() {
  if (!isNativeApp() || initialized) return;
  await initializeSecureStorage();
  await clearNativeDownloads();
  window.fetch = createNativeFetch(window.fetch.bind(window), apiOrigin(), nativeSession, () => {
    window.dispatchEvent(new Event(sessionExpiredEvent));
  });
  document.documentElement.classList.add("native-app");
  window.addEventListener(sessionClearedEvent, () => {
    void Promise.all([nativeSession.clear(), clearNativeLogin(), clearNativeDownloads()]).catch(() => reportNativeError("Sesioni nuk u fshi nga pajisja. Mbylle dhe hape aplikacionin përsëri."));
  });
  await App.addListener("appUrlOpen", event => {
    void openAppUrl(event.url).catch(() => reportNativeError("Kyçja nuk përfundoi. Provo përsëri."));
  });
  await App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) window.dispatchEvent(new Event(nativeResumeEvent));
  });
  await App.addListener("backButton", ({ canGoBack }) => {
    const event = new Event(nativeBackEvent, { cancelable: true });
    if (!window.dispatchEvent(event)) return;
    if (document.querySelector('[role="dialog"], [aria-modal="true"]')) {
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
      return;
    }
    if (canGoBack && window.location.pathname !== "/auth/callback") window.history.back();
    else void App.minimizeApp();
  });
  document.addEventListener("click", event => {
    const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const url = new URL(anchor.href);
    if (url.origin === apiOrigin() && url.pathname === "/api/auth/microsoft/start") {
      event.preventDefault();
      void openMicrosoftLoginUrl(url.href).catch(() => reportNativeError("Microsoft nuk u hap. Provo përsëri."));
    }
  });
  initialized = true;
  const launch = await App.getLaunchUrl();
  if (launch?.url) await openAppUrl(launch.url);
}

export async function openMicrosoftLoginUrl(value: string) {
  if (!isNativeApp()) { window.location.assign(value); return; }
  const url = new URL(value);
  if (url.origin !== apiOrigin() || url.pathname !== "/api/auth/microsoft/start") throw new Error("Adresa e kyçjes nuk është e vlefshme.");
  const { verifier, challenge } = await createNativeLoginChallenge();
  const returnTo = url.searchParams.get("return_to");
  const callbackQuery = returnTo ? new URL(returnTo).searchParams.toString() : "";
  await storeNativeLogin(verifier, callbackQuery);
  url.searchParams.set("native_challenge", challenge);
  try { await Browser.open({ url: url.href }); }
  catch { await clearNativeLogin(); throw new Error("Microsoft nuk u hap. Provo përsëri."); }
}

export async function startNativeMicrosoftLogin(email: string, returnPath?: string) {
  const callback = new URL("/auth/callback", "https://localhost");
  if (returnPath) callback.searchParams.set("app_return_to", returnPath);
  const params = new URLSearchParams({ email, return_to: callback.href });
  await openMicrosoftLoginUrl(`${apiOrigin()}/api/auth/microsoft/start?${params}`);
}

export async function takeNativeLoginVerifier() {
  const login = await readNativeLogin();
  if (!login) throw new Error("Kyçja ka skaduar. Kthehu dhe provo përsëri.");
  return login.verifier;
}
export const completeNativeLogin = clearNativeLogin;
